import { uniqueId } from 'lodash';
import React, { createContext, useContext, useState } from 'react';

import { confirm } from '@dashboard/lib/confirm';

import useProduct from '../../useProduct';

import { getInitialVariantOption } from './useVariantOption';
import { useVariants } from './VariantsContext';

const VariantEditContext = createContext();

export function VariantEditProvider({ children, onChange }) {
  const { product, updateProduct } = useProduct();

  const { state: variants, dispatch } = useVariants();

  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const [isAddingVariant, setIsAddingVariant] = useState(false);

  async function onReorderVariants(items) {
    const initialVariants = [...variants];

    dispatch({ type: 'set', values: items });

    const { data, error } = await updateProduct.mutateAsync({ variants: items });

    if (!data || error) {
      dispatch({ type: 'set', values: initialVariants });
    } else {
      if (onChange) {
        onChange(data);
      }
    }
  }

  async function setIsEditingVariant(isEditing, variantId) {
    // if adding variant, don't allow opening other variants
    // or closing the current one
    if (isAddingVariant) return;

    let proceed = true;
    // if not editing and has unsaved changes, ask for confirmation before closing
    if (hasUnsavedChanges) {
      proceed = await confirm('Discard unsaved changes?', {
        confirm: 'Discard',
      });
    }

    if (!proceed) return;

    setHasUnsavedChanges(false);

    dispatch({
      type: 'set',
      // open current variant and close all others
      values: variants.map(v => ({
        ...v,
        isEditing: v._id === variantId || v.key === variantId ? isEditing : false,
      })),
    });
  }

  async function onAddVariant() {
    let proceed = true;

    if (hasUnsavedChanges) {
      proceed = await confirm('Discard unsaved changes?', {
        confirm: 'Discard',
      });
    }

    if (!proceed) return;

    dispatch({
      type: 'add',
      values: [
        {
          key: uniqueId(),
          name: '',
          required: true,
          status: 'enabled',
          manageVariantOptionStock: true,
          options: [getInitialVariantOption(product)],
          selectionType: 'single',
          showHideLogic: {
            enabled: false,
            comparison: 'IS',
            value: '',
            element: {
              elementType: 'variant',
              elementId: '',
              elementTitle: '',
            },
          },
          isEditing: true,
          isNew: true,
        },
      ],
    });

    setIsAddingVariant(true);
    setHasUnsavedChanges(false);
  }

  return (
    <VariantEditContext.Provider
      value={{
        onChange,
        onAddVariant,
        setIsEditingVariant,
        isAddingVariant,
        setIsAddingVariant,
        hasUnsavedChanges,
        setHasUnsavedChanges,
        onReorderVariants,
      }}
    >
      {children}
    </VariantEditContext.Provider>
  );
}

export const useVariantEdit = () => useContext(VariantEditContext);
